import type { CamelCasedProperties, SnakeCasedProperties } from 'type-fest'
import type {
  GameDayScenarioRun,
  RequestSource
} from 'types/api/gamedays/gamedays'
import type { TriggerSourceKey } from 'types/api/runs-common'
import type { BaseGraph, GraphNode } from 'types/api/scenarios/scenario'

export interface BaseScenarioRunGraph extends BaseGraph {
  nodes_recursive?: [GraphNode]
  current_id?: string
}

export type ScenarioRunGraph = BaseScenarioRunGraph &
  CamelCasedProperties<BaseScenarioRunGraph>

export interface ScenarioRunInput {
  hypothesis?: string
}

export type ScenarioRunStatus = 'Passed' | 'Unsure' | 'Failed'

export interface ScenarioRunResults {
  incidentDetected?: boolean
  incidentMitigated?: boolean
  status?: ScenarioRunStatus
}

export enum ScenarioRunStage {
  SETUP = 'Setup',
  ACTIVE = 'Active',
  SETUP_FAILED = 'SetupFailed',
  FAILED = 'Failed',
  HALTED = 'Halted',
  COMPLETED = 'Completed'
}

export interface ScenarioRunStageInfo {
  currentNodeId?: string
  stage: ScenarioRunStage
  stageMetadata: { [key: string]: any }
}

// This is sometimes camelCase and sometimes snake_case depending on the endpoint :(
export interface ScenarioRunResponse {
  createdAt: string
  createdBy: string
  createdByFirstName?: string
  createdByLastName?: string
  createSource: RequestSource
  description?: string
  endTime?: string
  gameDayInfo?: {
    guid: string
    name: string
  }
  graph: ScenarioRunGraph
  hypothesis?: string
  name: string
  orgId?: string
  reliabilityTestId?: string
  resolvedTargets?: Array<Array<{ [key: string]: any }>>
  resultFlags?: { [key: string]: boolean }
  resultNotes?: string
  results?: ScenarioRunResults
  runNumber?: number
  scenarioId?: string
  scenarioRunImages?: string[]
  scheduleId?: string
  serviceId?: string
  stageInfo?: ScenarioRunStageInfo
  stepSummaries?: Array<{ [key: string]: any }>
  triggerSource?: TriggerSourceKey
  updatedAt?: string
}

// accounting for possibility of it being returned with either casing
export type ScenarioRun = ScenarioRunResponse &
  SnakeCasedProperties<ScenarioRunResponse> &
  GameDayScenarioRun
